import * as XLSX from 'xlsx';

/**
 * Kat Karşılığı → Arsa Ortakları ve Bölümler için Excel şablonu indirme / yükleme.
 * Şablon mevcut kayıtları ID'leriyle birlikte içerir: ID'li satırlar güncellenir, ID'si boş
 * satırlar yeni kayıt olarak eklenir. Excel'den silinen satırlar sistemden SİLİNMEZ.
 * Blok / kat / cephe adla yazılır ve yüklemede bu arsadaki ID'lere çevrilir.
 */

interface OrtakKaynak { ortakID: number; adSoyad: string; tcKimlik: string | null; telefon: string | null; eposta: string | null; hisseMetre: number | null; not: string | null; }
interface KatKaynak { katID: number; katAdi: string; siraNo: number; }
interface BlokKaynak { blokID: number; blokAdi: string; katlar: KatKaynak[]; }
interface CepheKaynak { cepheID: number; cepheAdi: string; }
interface BolumKaynak {
    bolumID: number; bolumAdi: string; blokID: number | null; katID: number | null;
    cephe1ID: number | null; cephe2ID: number | null; cephe3ID: number | null; cephe4ID: number | null;
    bolumTipi: string | null; turu: string | null; m2: number; fiyat: number; not: string | null;
}
export interface ArsaKaynak {
    takipNo: string;
    ortaklar: OrtakKaynak[];
    bloklar: BlokKaynak[];
    cepheler: CepheKaynak[];
    bolumler: BolumKaynak[];
}

export interface OrtakSatir { ortakID: number | null; adSoyad: string; tcKimlik: string | null; telefon: string | null; eposta: string | null; hisseMetre: number | null; not: string | null; }
export interface BolumSatir {
    bolumID: number | null; bolumAdi: string; blokID: number | null; katID: number | null;
    cephe1ID: number | null; cephe2ID: number | null; cephe3ID: number | null; cephe4ID: number | null;
    bolumTipi: string | null; turu: string | null; m2: number; fiyat: number; not: string | null;
}
export interface OkumaSonucu<T> { satirlar: T[]; hatalar: string[]; }

const ORTAK_BASLIK = ['ID', 'Ad Soyad *', 'TC Kimlik', 'Telefon', 'E-Posta', 'Hisse m²', 'Not'];
const BOLUM_BASLIK = ['ID', 'Bölüm Adı *', 'Blok', 'Kat', 'Cephe 1', 'Cephe 2', 'Cephe 3', 'Cephe 4', 'Bölüm Tipi', 'Türü', 'm²', 'Fiyat (TL)', 'Not'];

// Türkçe küçük harf: "ID" → "ıd" olur; ı/i farkı yok sayılır ki "ID", "Id", "id" aynı eşleşsin.
const norm = (s: unknown) => String(s ?? '').replace(/\*/g, '').trim().toLocaleLowerCase('tr-TR').replace(/ı/g, 'i');
const metin = (v: unknown): string | null => {
    const s = String(v ?? '').trim();
    return s === '' ? null : s;
};
/** Excel hücresi sayı ya da "1.250,50" / "3.250.000" / "1250.50" biçiminde metin olabilir. */
const sayi = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return isFinite(v) ? v : null;
    let s = String(v).replace(/[^\d.,-]/g, '');
    if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
    else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, ''); // nokta binlik ayracı
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
};

const sayfaYaz = (wb: XLSX.WorkBook, ad: string, satirlar: (string | number | null)[][], genislikler: number[]) => {
    const ws = XLSX.utils.aoa_to_sheet(satirlar);
    ws['!cols'] = genislikler.map(wch => ({ wch }));
    XLSX.utils.book_append_sheet(wb, ws, ad);
};

const aciklamaSayfasi = (wb: XLSX.WorkBook, ekSatirlar: string[]) => sayfaYaz(wb, 'Açıklama', [
    ['Nasıl kullanılır?'],
    ['• ID sütunu dolu satırlar sistemdeki kaydı GÜNCELLER; ID boş satırlar YENİ kayıt olarak eklenir.'],
    ['• ID değerini değiştirmeyin. Excel\'den satır silmek, sistemdeki kaydı silmez.'],
    ['• * işaretli sütunlar zorunludur. Başlık satırını değiştirmeyin.'],
    ...ekSatirlar.map(s => [s]),
], [110]);

/** Yüklenen dosyanın ilk sayfasını başlık satırına göre nesnelere çevirir (başlıklar küçük harf + * temizlenmiş). */
const sayfaOku = async (file: File): Promise<Record<string, unknown>[]> => {
    const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const ham = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '', raw: true });
    return ham.map(r => {
        const o: Record<string, unknown> = {};
        Object.keys(r).forEach(k => { o[norm(k)] = r[k]; });
        return o;
    }).filter(r => Object.values(r).some(v => String(v ?? '').trim() !== ''));
};

/** Satırdan sütun değeri — başlık adı, okunan başlıklarla aynı biçimde normalleştirilir. */
const al = (r: Record<string, unknown>, ...basliklar: string[]): unknown => {
    for (const b of basliklar) { const v = r[norm(b)]; if (v !== undefined && v !== '') return v; }
    return undefined;
};

const dosyaAdi = (tur: string, takipNo: string) => `${tur}_${takipNo || 'arsa'}.xlsx`;

// ── Arsa Ortakları ────────────────────────────────────────────────────────────

export const ortakSablonIndir = (arsa: ArsaKaynak) => {
    const wb = XLSX.utils.book_new();
    sayfaYaz(wb, 'Ortaklar', [
        ORTAK_BASLIK,
        ...arsa.ortaklar.map(o => [o.ortakID, o.adSoyad, o.tcKimlik, o.telefon, o.eposta, o.hisseMetre, o.not]),
    ], [8, 28, 14, 16, 26, 12, 30]);
    aciklamaSayfasi(wb, ['• Hisse m² sayı olarak girilir (örn. 125,5). Hisse oranı arsa m²\'sine göre otomatik hesaplanır.']);
    XLSX.writeFile(wb, dosyaAdi('ArsaOrtaklari', arsa.takipNo));
};

export const ortakExcelOku = async (file: File, arsa: ArsaKaynak): Promise<OkumaSonucu<OrtakSatir>> => {
    const satirlar: OrtakSatir[] = [];
    const hatalar: string[] = [];
    const idler = new Set(arsa.ortaklar.map(o => o.ortakID));
    (await sayfaOku(file)).forEach((r, i) => {
        const no = i + 2; // Excel satır numarası (başlık 1. satır)
        const id = sayi(al(r, 'id'));
        const adSoyad = metin(al(r, 'ad soyad'));
        if (!adSoyad) { hatalar.push(`Satır ${no}: Ad Soyad boş.`); return; }
        if (id !== null && !idler.has(id)) { hatalar.push(`Satır ${no}: ID ${id} bu arsanın ortaklarında yok (yeni kayıt için ID'yi boş bırakın).`); return; }
        const hisseHam = al(r, 'hisse m²', 'hisse m2');
        const hisseMetre = sayi(hisseHam);
        if (metin(hisseHam) && hisseMetre === null) { hatalar.push(`Satır ${no}: Hisse m² sayı değil.`); return; }
        satirlar.push({
            ortakID: id, adSoyad, hisseMetre,
            tcKimlik: metin(al(r, 'tc kimlik')), telefon: metin(al(r, 'telefon')), eposta: metin(al(r, 'e-posta')), not: metin(al(r, 'not')),
        });
    });
    return { satirlar, hatalar };
};

// ── Bölümler ──────────────────────────────────────────────────────────────────

export const bolumSablonIndir = (arsa: ArsaKaynak) => {
    const blokAdi = (id: number | null) => arsa.bloklar.find(b => b.blokID === id)?.blokAdi ?? null;
    const katAdi = (id: number | null) => arsa.bloklar.flatMap(b => b.katlar).find(k => k.katID === id)?.katAdi ?? null;
    const cepheAdi = (id: number | null) => arsa.cepheler.find(c => c.cepheID === id)?.cepheAdi ?? null;
    const wb = XLSX.utils.book_new();
    sayfaYaz(wb, 'Bölümler', [
        BOLUM_BASLIK,
        ...arsa.bolumler.map(b => [
            b.bolumID, b.bolumAdi, blokAdi(b.blokID), katAdi(b.katID),
            cepheAdi(b.cephe1ID), cepheAdi(b.cephe2ID), cepheAdi(b.cephe3ID), cepheAdi(b.cephe4ID),
            b.bolumTipi, b.turu, b.m2, b.fiyat, b.not,
        ]),
    ], [8, 22, 14, 14, 14, 14, 14, 14, 14, 16, 10, 16, 30]);
    // Geçerli blok / kat / cephe adları — yazımın birebir aynı olması gerekir
    const katSatirlari: (string | number | null)[][] = [['Blok', 'Kat']];
    arsa.bloklar.forEach(b => [...b.katlar].sort((x, y) => x.siraNo - y.siraNo).forEach(k => katSatirlari.push([b.blokAdi, k.katAdi])));
    sayfaYaz(wb, 'Bloklar ve Katlar', katSatirlari, [20, 20]);
    sayfaYaz(wb, 'Cepheler', [['Cephe'], ...arsa.cepheler.map(c => [c.cepheAdi])], [24]);
    aciklamaSayfasi(wb, [
        '• Blok, Kat ve Cephe adları "Bloklar ve Katlar" ve "Cepheler" sayfalarındaki adlarla aynı yazılmalıdır.',
        '• Kat, yazılan bloğun katları arasından bulunur; blok boşsa kat adı tüm bloklarda tek olmalıdır.',
        '• m² ve Fiyat sayı olarak girilir; birim fiyat otomatik hesaplanır.',
    ]);
    XLSX.writeFile(wb, dosyaAdi('Bolumler', arsa.takipNo));
};

export const bolumExcelOku = async (file: File, arsa: ArsaKaynak): Promise<OkumaSonucu<BolumSatir>> => {
    const satirlar: BolumSatir[] = [];
    const hatalar: string[] = [];
    const idler = new Set(arsa.bolumler.map(b => b.bolumID));
    const esit = (a: string, b: string) => norm(a) === norm(b);

    (await sayfaOku(file)).forEach((r, i) => {
        const no = i + 2;
        const satirHata: string[] = [];
        const id = sayi(al(r, 'id'));
        const bolumAdi = metin(al(r, 'bölüm adı'));
        if (!bolumAdi) { hatalar.push(`Satır ${no}: Bölüm Adı boş.`); return; }
        if (id !== null && !idler.has(id)) { hatalar.push(`Satır ${no}: ID ${id} bu arsanın bölümlerinde yok (yeni kayıt için ID'yi boş bırakın).`); return; }

        const blokYazi = metin(al(r, 'blok'));
        const blok = blokYazi ? arsa.bloklar.find(b => esit(b.blokAdi, blokYazi)) : undefined;
        if (blokYazi && !blok) satirHata.push(`blok "${blokYazi}" bulunamadı`);

        let katID: number | null = null;
        const katYazi = metin(al(r, 'kat'));
        if (katYazi) {
            const adaylar = (blok ? [blok] : arsa.bloklar)
                .flatMap(b => b.katlar.filter(k => esit(k.katAdi, katYazi)).map(k => k.katID));
            if (adaylar.length === 1) katID = adaylar[0];
            else if (adaylar.length === 0) satirHata.push(`kat "${katYazi}" ${blok ? `"${blok.blokAdi}" bloğunda ` : ''}bulunamadı`);
            else satirHata.push(`kat "${katYazi}" birden fazla blokta var, blok yazın`);
        }
        // Kat verildi ama blok verilmediyse bloğu kattan çıkar
        const blokID = blok?.blokID ?? (katID !== null ? arsa.bloklar.find(b => b.katlar.some(k => k.katID === katID))?.blokID ?? null : null);

        const cephe = (sutun: string): number | null => {
            const yazi = metin(al(r, sutun));
            if (!yazi) return null;
            const c = arsa.cepheler.find(x => esit(x.cepheAdi, yazi));
            if (!c) { satirHata.push(`cephe "${yazi}" bulunamadı`); return null; }
            return c.cepheID;
        };
        const cephe1ID = cephe('cephe 1'), cephe2ID = cephe('cephe 2'), cephe3ID = cephe('cephe 3'), cephe4ID = cephe('cephe 4');

        const m2Ham = al(r, 'm²', 'm2');
        const fiyatHam = al(r, 'fiyat (tl)', 'fiyat');
        const m2 = sayi(m2Ham), fiyat = sayi(fiyatHam);
        if (metin(m2Ham) && m2 === null) satirHata.push('m² sayı değil');
        if (metin(fiyatHam) && fiyat === null) satirHata.push('Fiyat sayı değil');

        if (satirHata.length) { hatalar.push(`Satır ${no} (${bolumAdi}): ${satirHata.join('; ')}.`); return; }
        satirlar.push({
            bolumID: id, bolumAdi, blokID, katID, cephe1ID, cephe2ID, cephe3ID, cephe4ID,
            bolumTipi: metin(al(r, 'bölüm tipi')), turu: metin(al(r, 'türü')), m2: m2 ?? 0, fiyat: fiyat ?? 0, not: metin(al(r, 'not')),
        });
    });
    return { satirlar, hatalar };
};
